import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './sidebar';
import ChangingModalDateofDeliveryOrders from './modal_windows/ChangingModalDateofDeliveryOrders';
import ModalDeliveryOrdersCanceled from './modal_windows/ModalDeliveryOrdersCanceled';
import ConfirmCompletionModal from './modal_windows/ConfirmCompletionModal';
import OrderQuickView from './modal_windows/OrderQuickView';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';

import {
  StatCard, RefreshButton, SearchField, FilterPill,
  StatusBadge, TableCard, LoadingState, EmptyState,
  Pagination, BulkBar,
} from '../../ui/CrmPage';

const PAGE_SIZE = 15;
const KZT = new Intl.NumberFormat('ru-KZ', { maximumFractionDigits: 0 });

const formatDate = (str) => {
  if (!str) return '—';
  const d = new Date(str);
  if (isNaN(d.getTime())) return '—';
  return `${d.getUTCDate().toString().padStart(2, '0')}.${(d.getUTCMonth() + 1).toString().padStart(2, '0')}.${d.getUTCFullYear()}`;
};

const OrdersOnDelivery = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isChangingDateModalOpen, setIsChangingDateModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Все');
  const [regionFilter, setRegionFilter] = useState('Все');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchWorkOrders();
    const intervalId = setInterval(fetchWorkOrders, 30000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const fetchWorkOrders = () => {
    setIsLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}v1/orders/delivery`)
      .then(res => res.json())
      .then(data => setWorkOrders(Array.isArray(data) ? data : []))
      .catch(err => console.error('Ошибка загрузки delivery orders:', err))
      .finally(() => setIsLoading(false));
  };

  const handleCheckboxChange = (id) => {
    setSelectedOrders(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const updateOrderStatus = async (id, status, date = null) => {
    const order = workOrders.find(o => o.id === id);
    if (!order) throw new Error('Заказ не найден');
    const localDate = date ? new Date(new Date(date).getTime() - new Date().getTimezoneOffset() * 60000) : null;
    const updated = {
      delivery_address: order.delivery_address,
      delivery_date: localDate ? localDate.toISOString().split('T')[0] : order.delivery_date,
      id_status: status !== null ? status : order.id_status,
      id_payment_status: order.id_payment_status,
      total_amount: order.total_amount,
      id_organization: order.id_organization,
      id_division: order.id_division || null,
      id_drivers: order.id_drivers || null,
      week_number: order.week_number,
      id_contracts: order.id_contracts,
      id_client: order.id_client,
      id_user: order.id_user,
      id_region: order.id_region,
    };
    const res = await fetch(`${process.env.REACT_APP_API_URL}v1/orders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updated),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Ошибка обновления');
    }
    const updatedOrder = await res.json();
    setWorkOrders(prev => prev.map(o => o.id === id ? { ...o, ...updatedOrder } : o));
  };

  const handleBulkUpdate = async (status, date = null) => {
    setIsLoading(true);
    try {
      await Promise.all(selectedOrders.map(id => updateOrderStatus(id, status, date)));
      setSelectedOrders([]);
    } catch (e) {
      alert(`Ошибка: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmCompletion = () => { handleBulkUpdate(13); setIsConfirmModalOpen(false); };
  const handleCancelOrder      = () => { handleBulkUpdate(9);  setIsCancelModalOpen(false); };
  const handleDateChange       = (d) => { handleBulkUpdate(null, d); setIsChangingDateModalOpen(false); };

  // --- summary
  const summary = useMemo(() => {
    const total = workOrders.length;
    const inTransit = workOrders.filter(o => /в пути|доставке/i.test(o.status_name || '')).length;
    const delivered = workOrders.filter(o => /доставлен/i.test(o.status_name || '')).length;
    const issues = workOrders.filter(o => /передел|накладн|документ/i.test(o.status_name || '')).length;
    return { total, inTransit, delivered, issues };
  }, [workOrders]);

  const statusGroups = useMemo(() => {
    return Array.from(new Set(workOrders.map(o => o.status_name).filter(Boolean)));
  }, [workOrders]);

  const regionGroups = useMemo(() => {
    return Array.from(new Set(workOrders.map((o) => o.region_name).filter(Boolean))).sort();
  }, [workOrders]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return workOrders.filter(o => {
      if (statusFilter !== 'Все' && o.status_name !== statusFilter) return false;
      if (regionFilter !== 'Все' && (o.region_name || '') !== regionFilter) return false;
      if (!term) return true;
      return (
        (o.client_name || '').toLowerCase().includes(term) ||
        (o.delivery_address || '').toLowerCase().includes(term) ||
        (o.expediter || '').toLowerCase().includes(term) ||
        String(o.order_number || '').includes(term)
      );
    });
  }, [workOrders, searchTerm, statusFilter, regionFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paginatedOrders = filteredOrders.slice(pageStart, pageStart + PAGE_SIZE);

  const resetFilters = () => { setSearchTerm(''); setStatusFilter('Все'); setRegionFilter('Все'); setCurrentPage(1); };
  const hasFilters = searchTerm || statusFilter !== 'Все' || regionFilter !== 'Все';
  const paymentLabel = (s) => s === 2 ? 'Оплачен' : s === 1 ? 'Ожидает оплаты' : '—';

  return (
    <div className="order-on-delivery-basic-container crmpage">
      <div className="order-on-delivery-sidebar-container">
        <Sidebar />
      </div>

      <div className="order-on-delivery-basic-content crmpage__main">
        <header className="crmpage__head">
          <h1 className="crmpage__title">Заказы на доставке</h1>
        </header>

        <section className="crmpage__stats">
          <StatCard label="Всего"      value={summary.total}     accent="neutral" />
          <StatCard label="В пути"     value={summary.inTransit} accent="info" />
          <StatCard label="Доставлено" value={summary.delivered} accent="success" />
          <StatCard label="Проблемные" value={summary.issues}    accent="warning" />
          <RefreshButton onClick={() => setReloadKey(k => k + 1)} />
        </section>

        <section className="crmpage__filters">
          <SearchField
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Поиск: номер, клиент, адрес, экспедитор…"
          />
          <div className="crmpage__pills">
            <FilterPill
              active={statusFilter === 'Все'}
              onClick={() => { setStatusFilter('Все'); setCurrentPage(1); }}
              count={workOrders.length}
            >Все</FilterPill>
            {statusGroups.map(s => (
              <FilterPill key={s}
                active={statusFilter === s}
                onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
                count={workOrders.filter(o => o.status_name === s).length}
              >{s}</FilterPill>
            ))}
          </div>
          {hasFilters && (
            <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
          )}
        </section>

        <section className="crmpage__filters crmpage__filters--regions">
          <div className="crmpage__pills">
            <FilterPill
              active={regionFilter === 'Все'}
              onClick={() => { setRegionFilter('Все'); setCurrentPage(1); }}
              count={workOrders.length}
            >Все регионы</FilterPill>
            {regionGroups.map((r) => (
              <FilterPill
                key={r}
                active={regionFilter === r}
                onClick={() => { setRegionFilter(r); setCurrentPage(1); }}
                count={workOrders.filter((o) => (o.region_name || '') === r).length}
              >{r}</FilterPill>
            ))}
          </div>
        </section>

        <TableCard>
          {isLoading && workOrders.length === 0 ? (
            <LoadingState text="Загружаем заказы на доставке…" />
          ) : paginatedOrders.length === 0 ? (
            <EmptyState title="Заказов нет" text={hasFilters ? 'Сбросьте фильтры.' : 'Здесь появятся заказы, переданные на доставку.'} />
          ) : (
            <>
              <div className="crmpage__table-wrap">
                <table className="crmpage__table">
                  <colgroup>
                    <col style={{ width: 40 }} />
                    <col style={{ width: 80 }} />
                    <col />
                    <col />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 100 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="crmpage__th-check"></th>
                      <th>Номер</th>
                      <th>Клиент</th>
                      <th className="crmpage__hide-880">Экспедитор</th>
                      <th>Статус</th>
                      <th className="crmpage__hide-1180">Сумма</th>
                      <th className="crmpage__hide-1180">Оплата</th>
                      <th className="crmpage__hide-1000">Доставка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.map((order) => {
                      const checked = selectedOrders.includes(order.id);
                      return (
                        <tr
                          key={order.id}
                          className={checked ? 'crmpage__row crmpage__row--selected' : 'crmpage__row'}
                          onClick={() => setDetailRow(order)}
                        >
                          <td className="crmpage__td-check" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" className="crmpage__check"
                              checked={checked} disabled={isLoading}
                              onChange={() => handleCheckboxChange(order.id)} />
                          </td>
                          <td><span className="crmpage__num">№{order.order_number}</span></td>
                          <td>
                            <div className="crmpage__primary">{order.client_name || '—'}</div>
                            <div className="crmpage__sub">{order.delivery_address}</div>
                            {order.region_name && (
                              <span className="crmpage__chip crmpage__chip--region">{order.region_name}</span>
                            )}
                          </td>
                          <td className="crmpage__muted crmpage__hide-880">{order.expediter || '—'}</td>
                          <td><StatusBadge name={order.status_name} /></td>
                          <td className="crmpage__date crmpage__hide-1180">{KZT.format(order.total_amount || 0)} ₸</td>
                          <td className="crmpage__hide-1180"><StatusBadge name={paymentLabel(order.id_payment_status)} /></td>
                          <td className="crmpage__date crmpage__hide-1000">{formatDate(order.delivery_date)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="crmpage__cards">
                {paginatedOrders.map((o) => {
                  const checked = selectedOrders.includes(o.id);
                  return (
                    <div key={o.id} className={`crmpage__card ${checked ? 'is-selected' : ''}`} onClick={() => setDetailRow(o)}>
                      <div className="crmpage__card-head">
                        <label className="crmpage__card-check" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" className="crmpage__check"
                            checked={checked} onChange={() => handleCheckboxChange(o.id)} />
                          <span className="crmpage__num">№{o.order_number}</span>
                        </label>
                        <StatusBadge name={o.status_name} />
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Клиент</span>
                        <span className="crmpage__card-val">{o.client_name || '—'}</span>
                      </div>
                      {o.region_name && (
                        <div className="crmpage__card-row">
                          <span className="crmpage__card-label">Регион</span>
                          <span className="crmpage__card-val">{o.region_name}</span>
                        </div>
                      )}
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Экспедитор</span>
                        <span className="crmpage__card-val">{o.expediter || '—'}</span>
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Доставка</span>
                        <span className="crmpage__card-val">{formatDate(o.delivery_date)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
            </>
          )}
        </TableCard>

        <BulkBar count={selectedOrders.length} onClear={() => setSelectedOrders([])}>
          <button className="crmpage__btn crmpage__btn--ghost" onClick={() => setIsChangingDateModalOpen(true)} disabled={isLoading}>
            <EventOutlinedIcon sx={{ fontSize: 18 }} /> Изменить дату
          </button>
          <button className="crmpage__btn crmpage__btn--ghost" onClick={() => setIsCancelModalOpen(true)} disabled={isLoading}>
            <CancelOutlinedIcon sx={{ fontSize: 18 }} /> Отменить
          </button>
          <button className="crmpage__btn crmpage__btn--primary" onClick={() => setIsConfirmModalOpen(true)} disabled={isLoading}>
            <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> Подтвердить завершение
          </button>
        </BulkBar>

        {isChangingDateModalOpen && (
          <ChangingModalDateofDeliveryOrders
            onClose={() => setIsChangingDateModalOpen(false)}
            onConfirm={handleDateChange}
          />
        )}
        {isCancelModalOpen && (
          <ModalDeliveryOrdersCanceled
            onClose={() => setIsCancelModalOpen(false)}
            onConfirm={handleCancelOrder}
          />
        )}
        {isConfirmModalOpen && (
          <ConfirmCompletionModal
            onClose={() => setIsConfirmModalOpen(false)}
            onConfirm={handleConfirmCompletion}
          />
        )}

        <OrderQuickView
          isOpen={!!detailRow}
          row={detailRow}
          onClose={() => setDetailRow(null)}
        />
      </div>
    </div>
  );
};

export default OrdersOnDelivery;
